import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "fieldsContainer", "progressBar", "progressContainer",
    "stepLabel", "stepTitle", "stepTitleText", "stepDescText",
    "currentStepNum", "backButton", "nextButton", "submitButton",
    "welcomeSection", "keyboardHint"
  ]

  static values = {
    formData: Object,
    formType: String,
    answers: Object,
    currentStep: Number,
    submitUrl: String,
    saveDraftUrl: String,
    resultsUrl: String,
    submitted: Boolean
  }

  connect() {
    if (this.submittedValue) return

    this.answers = this.answersValue || {}
    this.currentStep = this.currentStepValue || 1
    this.fields = this.formDataValue?.fields || []
    this.steps = this.formDataValue?.steps || []
    this.submitting = false

    this.render()

    // Keyboard navigation for multi-step
    if (this.formTypeValue === "multi_step") {
      this.handleKeydown = (e) => {
        if (e.key === "Enter" && !e.shiftKey && e.target.tagName !== "TEXTAREA") {
          e.preventDefault()
          this.nextStep()
        }
      }
      document.addEventListener("keydown", this.handleKeydown)
    }
  }

  disconnect() {
    if (this.handleKeydown) {
      document.removeEventListener("keydown", this.handleKeydown)
    }
  }

  // ==================
  // Rendering
  // ==================

  render() {
    switch (this.formTypeValue) {
      case "multi_step":
        this.renderMultiStep()
        break
      case "polling":
        this.renderPolling()
        break
      default:
        this.renderRichForm()
    }

    // Auto-focus first input
    requestAnimationFrame(() => {
      const firstInput = this.fieldsContainerTarget.querySelector("input:not([type=hidden]):not([type=radio]):not([type=checkbox]), textarea")
      if (firstInput) firstInput.focus()
    })
  }

  renderMultiStep() {
    const step = this.steps.find(s => s.id === this.currentStep) || this.steps[0]
    if (!step) return

    const stepFields = this.fields.filter(f => f.step === step.id)
    const stepIdx = this.steps.findIndex(s => s.id === this.currentStep)
    const isFirst = stepIdx === 0
    const isLast = stepIdx === this.steps.length - 1

    // Update progress
    if (this.hasProgressBarTarget) {
      const pct = ((stepIdx + 1) / this.steps.length) * 100
      this.progressBarTarget.style.width = `${pct}%`
    }
    if (this.hasCurrentStepNumTarget) {
      this.currentStepNumTarget.textContent = stepIdx + 1
    }

    // Update step title
    if (this.hasStepTitleTextTarget) {
      this.stepTitleTextTarget.textContent = step.title || ""
    }
    if (this.hasStepDescTextTarget) {
      this.stepDescTextTarget.textContent = step.description || ""
    }

    // Show/hide nav buttons
    if (this.hasBackButtonTarget) {
      this.backButtonTarget.classList.toggle("hidden", isFirst)
      this.backButtonTarget.classList.toggle("flex", !isFirst)
    }
    if (this.hasNextButtonTarget) {
      this.nextButtonTarget.classList.toggle("hidden", isLast)
      this.nextButtonTarget.classList.toggle("flex", !isLast)
    }
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.classList.toggle("hidden", !isLast)
      this.submitButtonTarget.classList.toggle("flex", isLast)
    }

    // Render fields for this step
    this.renderFieldList(stepFields)
  }

  renderRichForm() {
    const visibleFields = this.getVisibleFields()
    this.renderFieldList(visibleFields)
  }

  renderPolling() {
    this.renderFieldList(this.fields, true)
  }

  getVisibleFields() {
    return this.fields.filter(field => {
      if (!field.conditional?.visible_when) return true
      const { field_id, operator, value } = field.conditional.visible_when
      if (!field_id) return true
      const answer = this.answers[field_id]
      switch (operator) {
        case "equals": return String(answer) === String(value)
        case "not_equals": return String(answer) !== String(value)
        case "contains": return String(answer || "").includes(value)
        case "in": return Array.isArray(answer) && answer.includes(value)
        default: return true
      }
    })
  }

  renderFieldList(fields, isPolling = false) {
    if (!this.hasFieldsContainerTarget) return

    this.fieldsContainerTarget.innerHTML = fields.map((field, idx) => {
      const globalIdx = this.fields.indexOf(field)
      if (isPolling) return this.renderPollField(field, globalIdx)
      return this.renderField(field, globalIdx)
    }).join("")
  }

  // ==================
  // Field Renderers
  // ==================

  renderField(field, fieldIndex) {
    const value = this.answers[field.id] || ""
    const requiredMark = field.required ? '<span class="text-error-500 ml-0.5">*</span>' : ""
    const num = fieldIndex + 1

    let input = ""
    switch (field.type) {
      case "text":
      case "email":
      case "url":
      case "number":
      case "phone":
        input = `<input type="${field.type === "phone" ? "tel" : field.type}" value="${this.esc(value)}"
                  data-field-id="${field.id}" data-action="input->form-renderer#updateAnswer"
                  class="w-full py-2 bg-transparent border-0 border-b-2 border-gray-200 text-xl text-gray-900 placeholder-gray-300 focus:border-brand-600 focus:ring-0 outline-none transition-colors"
                  placeholder="${this.esc(field.placeholder || "Type your answer here...")}">`
        break
      case "textarea":
        input = `<textarea data-field-id="${field.id}" data-action="input->form-renderer#updateAnswer"
                  class="w-full py-2 bg-transparent border-0 border-b-2 border-gray-200 text-xl text-gray-900 placeholder-gray-300 focus:border-brand-600 focus:ring-0 outline-none transition-colors min-h-28 resize-none"
                  placeholder="${this.esc(field.placeholder || "Type your answer here...")}">${this.esc(value)}</textarea>`
        break
      case "select":
        input = `<div class="flex flex-col gap-2.5">
          ${(field.options || []).filter(o => o.label).map((opt, i) => `
            <label class="group flex items-center gap-3 px-4 py-3 border-2 rounded-xl cursor-pointer transition-all ${
              value === opt.value
                ? "border-brand-600 bg-brand-50"
                : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
            }">
              <span class="w-6 h-6 flex items-center justify-center rounded text-xs font-bold flex-shrink-0 transition-colors ${
                value === opt.value ? "bg-brand-600 text-white" : "border border-gray-300 text-gray-500 group-hover:border-gray-400"
              }">
                ${String.fromCharCode(65 + i)}
              </span>
              <input type="radio" name="${field.id}" value="${this.esc(opt.value)}" ${value === opt.value ? "checked" : ""}
                     data-field-id="${field.id}" data-action="change->form-renderer#selectOption"
                     class="sr-only">
              <span class="text-base ${value === opt.value ? "font-medium text-gray-900" : "text-gray-700"}">${this.esc(opt.label)}</span>
            </label>
          `).join("")}
        </div>`
        break
      case "multi_select":
        const selected = Array.isArray(value) ? value : []
        input = `<div class="flex flex-col gap-2.5">
          ${(field.options || []).filter(o => o.label).map((opt, i) => `
            <label class="group flex items-center gap-3 px-4 py-3 border-2 rounded-xl cursor-pointer transition-all ${
              selected.includes(opt.value)
                ? "border-brand-600 bg-brand-50"
                : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
            }">
              <span class="w-6 h-6 flex items-center justify-center rounded text-xs font-bold flex-shrink-0 transition-colors ${
                selected.includes(opt.value) ? "bg-brand-600 text-white" : "border border-gray-300 text-gray-500 group-hover:border-gray-400"
              }">
                ${selected.includes(opt.value) ? '<svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/></svg>' : String.fromCharCode(65 + i)}
              </span>
              <input type="checkbox" value="${this.esc(opt.value)}" ${selected.includes(opt.value) ? "checked" : ""}
                     data-field-id="${field.id}" data-action="change->form-renderer#updateMultiAnswer"
                     class="sr-only">
              <span class="text-base ${selected.includes(opt.value) ? "font-medium text-gray-900" : "text-gray-700"}">${this.esc(opt.label)}</span>
            </label>
          `).join("")}
        </div>`
        break
      case "rating":
        const ratingVal = parseInt(value) || 0
        input = `<div class="flex items-center gap-2" data-field-id="${field.id}">
          ${[1,2,3,4,5].map(star => `
            <button type="button" data-action="click->form-renderer#setRating" data-field-id="${field.id}" data-rating="${star}"
                    class="w-10 h-10 flex items-center justify-center rounded-xl text-xl transition-all ${
                      star <= ratingVal
                        ? "text-amber-400 bg-amber-50 scale-110"
                        : "text-gray-300 hover:text-amber-300 hover:bg-amber-50/50"
                    }">
              ★
            </button>
          `).join("")}
          <span class="ml-3 text-sm font-medium ${ratingVal > 0 ? "text-gray-600" : "text-gray-400"}">${ratingVal > 0 ? ratingVal + " / 5" : ""}</span>
        </div>`
        break
      case "date":
        input = `<input type="date" value="${this.esc(value)}"
                  data-field-id="${field.id}" data-action="change->form-renderer#updateAnswer"
                  class="w-full py-2 bg-transparent border-0 border-b-2 border-gray-200 text-xl text-gray-900 focus:border-brand-600 focus:ring-0 outline-none transition-colors">`
        break
      case "checkbox":
        input = `<label class="group flex items-center gap-3 cursor-pointer py-1">
          <span class="w-6 h-6 flex items-center justify-center rounded border-2 flex-shrink-0 transition-colors ${
            value === true || value === "true"
              ? "border-brand-600 bg-brand-600"
              : "border-gray-300 group-hover:border-gray-400"
          }">
            ${value === true || value === "true" ? '<svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/></svg>' : ""}
          </span>
          <input type="checkbox" ${value === true || value === "true" ? "checked" : ""}
                 data-field-id="${field.id}" data-action="change->form-renderer#updateCheckbox"
                 class="sr-only">
          <span class="text-base text-gray-700">Check to agree</span>
        </label>`
        break
      case "yes_no":
        input = `<div class="inline-flex flex-col gap-2.5">
          ${[{id:"opt_0",label:"Yes",value:"yes"},{id:"opt_1",label:"No",value:"no"}].map((opt, i) => `
            <button type="button" data-action="click->form-renderer#selectYesNo" data-field-id="${field.id}" data-value="${this.esc(opt.value)}"
                    class="group flex items-center gap-3 px-4 py-3 border-2 rounded-xl text-left transition-all ${
                      value === opt.value
                        ? "border-brand-600 bg-brand-50"
                        : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                    }">
              <span class="w-6 h-6 flex items-center justify-center rounded text-xs font-bold flex-shrink-0 transition-colors ${
                value === opt.value ? "bg-brand-600 text-white" : "border border-gray-300 text-gray-500 group-hover:border-gray-400"
              }">${i === 0 ? "Y" : "N"}</span>
              <span class="text-base ${value === opt.value ? "font-medium text-gray-900" : "text-gray-700"}">${this.esc(opt.label)}</span>
            </button>
          `).join("")}
        </div>`
        break
      case "opinion_scale":
        const scaleMin = field.validation?.min_value || 1
        const scaleMax = field.validation?.max_value || 10
        const scaleVal = parseInt(value) || 0
        input = `<div class="flex flex-col gap-2">
          <div class="flex gap-1.5" data-field-id="${field.id}">
            ${Array.from({length: scaleMax - scaleMin + 1}, (_, i) => i + scaleMin).map(n => `
              <button type="button" data-action="click->form-renderer#setScale" data-field-id="${field.id}" data-scale="${n}"
                      class="flex-1 h-10 flex items-center justify-center rounded-lg text-sm font-medium transition-all ${
                        n === scaleVal
                          ? "bg-brand-600 text-white shadow-sm"
                          : n <= scaleVal && scaleVal > 0
                            ? "bg-brand-50 text-brand-600 border border-brand-200"
                            : "bg-gray-50 text-gray-500 border border-gray-200 hover:border-gray-300 hover:bg-white"
                      }">
                ${n}
              </button>
            `).join("")}
          </div>
          <div class="flex justify-between text-xs text-gray-400">
            <span>Not likely</span>
            <span>Very likely</span>
          </div>
        </div>`
        break
      case "file_upload":
        input = `<div class="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center hover:border-gray-300 transition-colors">
          <div class="flex flex-col items-center gap-2">
            <div class="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
              <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"/></svg>
            </div>
            <p class="text-sm text-gray-400">File upload</p>
          </div>
        </div>`
        break
      case "ranking":
        const rankOptions = (field.options || []).filter(o => o.label)
        const rankValue = Array.isArray(value) ? value : rankOptions.map(o => o.value)
        input = `<div class="flex flex-col gap-2.5" data-field-id="${field.id}" data-ranking-field>
          ${rankValue.map((val, i) => {
            const opt = rankOptions.find(o => o.value === val) || { label: val, value: val }
            return `
              <div class="flex items-center gap-3 px-4 py-3 border-2 border-gray-200 rounded-xl bg-white cursor-grab active:cursor-grabbing select-none transition-all"
                   draggable="true" data-rank-value="${this.esc(opt.value)}"
                   data-action="dragstart->form-renderer#rankDragStart dragover->form-renderer#rankDragOver dragend->form-renderer#rankDragEnd drop->form-renderer#rankDrop touchstart->form-renderer#rankTouchStart touchmove->form-renderer#rankTouchMove touchend->form-renderer#rankTouchEnd">
                <span class="w-6 h-6 flex items-center justify-center rounded bg-gray-100 text-xs font-bold text-gray-500">${i + 1}</span>
                <span class="flex-1 text-base text-gray-700">${this.esc(opt.label)}</span>
                <svg class="w-5 h-5 text-gray-300 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24"><circle cx="9" cy="6" r="1.5"/><circle cx="15" cy="6" r="1.5"/><circle cx="9" cy="12" r="1.5"/><circle cx="15" cy="12" r="1.5"/><circle cx="9" cy="18" r="1.5"/><circle cx="15" cy="18" r="1.5"/></svg>
              </div>
            `
          }).join("")}
        </div>`
        break
      default:
        input = `<input type="text" value="${this.esc(value)}"
                  data-field-id="${field.id}" data-action="input->form-renderer#updateAnswer"
                  class="w-full py-2 bg-transparent border-0 border-b-2 border-gray-200 text-xl text-gray-900 placeholder-gray-300 focus:border-brand-600 focus:ring-0 outline-none transition-colors"
                  placeholder="${this.esc(field.placeholder || "Type your answer here...")}">`
    }

    const description = field.description ? `<p class="text-text-sm text-gray-500 mt-1">${this.esc(field.description)}</p>` : ""

    return `
      <div class="flex flex-col gap-4" data-renderer-field-id="${field.id}">
        <div>
          <div class="flex items-baseline gap-2">
            <span class="text-base font-medium text-brand-600">${num}</span>
            <span class="text-brand-600">→</span>
            <h3 class="text-xl md:text-display-xs font-semibold text-gray-900">${this.esc(field.label)}${requiredMark}</h3>
          </div>
          ${description}
        </div>
        ${input}
      </div>
    `
  }

  renderPollField(field, fieldIndex) {
    if (!["select", "multi_select"].includes(field.type)) return this.renderField(field, fieldIndex)

    const value = this.answers[field.id] || ""
    const selected = Array.isArray(value) ? value : (value ? [value] : [])
    const isMulti = field.type === "multi_select"
    const num = fieldIndex + 1

    return `
      <div class="flex flex-col gap-4" data-renderer-field-id="${field.id}">
        <div class="flex items-baseline gap-2">
          <span class="text-base font-medium text-brand-600">${num}</span>
          <span class="text-brand-600">→</span>
          <h3 class="text-xl md:text-display-xs font-semibold text-gray-900">${this.esc(field.label)}</h3>
        </div>
        <div class="flex flex-col gap-2.5">
          ${(field.options || []).filter(o => o.label).map((opt, i) => `
            <button type="button" data-action="click->form-renderer#selectPollOption"
                    data-field-id="${field.id}" data-option-value="${this.esc(opt.value)}" data-multi="${isMulti}"
                    class="group flex items-center gap-3 px-4 py-3 border-2 rounded-xl text-left transition-all ${
                      selected.includes(opt.value)
                        ? "border-brand-600 bg-brand-50"
                        : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                    }">
              <span class="w-6 h-6 flex items-center justify-center rounded text-xs font-bold flex-shrink-0 transition-colors ${
                selected.includes(opt.value) ? "bg-brand-600 text-white" : "border border-gray-300 text-gray-500 group-hover:border-gray-400"
              }">
                ${selected.includes(opt.value) ? '<svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/></svg>' : String.fromCharCode(65 + i)}
              </span>
              <span class="text-base ${selected.includes(opt.value) ? "font-medium text-gray-900" : "text-gray-700"}">${this.esc(opt.label)}</span>
            </button>
          `).join("")}
        </div>
      </div>
    `
  }

  // ==================
  // Answer Handlers
  // ==================

  updateAnswer(e) {
    const fieldId = e.currentTarget.dataset.fieldId
    const oldVisibleIds = this.formTypeValue === "rich_form" ? this.getVisibleFields().map(f => f.id) : null
    this.answers[fieldId] = e.currentTarget.value
    // Only re-render if conditional visibility changed (fields shown/hidden)
    // Avoid re-render on every keystroke which destroys DOM and resets cursor
    if (oldVisibleIds) {
      const newVisibleIds = this.getVisibleFields().map(f => f.id)
      if (JSON.stringify(oldVisibleIds) !== JSON.stringify(newVisibleIds)) {
        this.render()
      }
    }
  }

  selectOption(e) {
    const fieldId = e.currentTarget.dataset.fieldId
    this.answers[fieldId] = e.currentTarget.value
    this.render()
  }

  updateMultiAnswer(e) {
    const fieldId = e.currentTarget.dataset.fieldId
    const value = e.currentTarget.value
    const current = Array.isArray(this.answers[fieldId]) ? [...this.answers[fieldId]] : []

    if (e.currentTarget.checked) {
      if (!current.includes(value)) current.push(value)
    } else {
      const idx = current.indexOf(value)
      if (idx > -1) current.splice(idx, 1)
    }

    this.answers[fieldId] = current
    this.render()
  }

  updateCheckbox(e) {
    const fieldId = e.currentTarget.dataset.fieldId
    this.answers[fieldId] = e.currentTarget.checked
    this.render()
  }

  setRating(e) {
    const fieldId = e.currentTarget.dataset.fieldId
    const rating = parseInt(e.currentTarget.dataset.rating)
    this.answers[fieldId] = rating
    this.render()
  }

  selectYesNo(e) {
    const fieldId = e.currentTarget.dataset.fieldId
    this.answers[fieldId] = e.currentTarget.dataset.value
    this.render()
  }

  setScale(e) {
    const fieldId = e.currentTarget.dataset.fieldId
    this.answers[fieldId] = parseInt(e.currentTarget.dataset.scale)
    this.render()
  }

  // ==================
  // Ranking Drag & Drop
  // ==================

  rankDragStart(e) {
    const item = e.currentTarget
    this._dragItem = item
    item.classList.add("opacity-50", "scale-95")
    e.dataTransfer.effectAllowed = "move"
  }

  rankDragOver(e) {
    e.preventDefault()
    e.dataTransfer.dropEffect = "move"
    const item = e.currentTarget
    if (!this._dragItem || item === this._dragItem) return

    const container = item.closest("[data-ranking-field]")
    const items = [...container.children]
    const dragIdx = items.indexOf(this._dragItem)
    const overIdx = items.indexOf(item)

    if (dragIdx < overIdx) {
      container.insertBefore(this._dragItem, item.nextSibling)
    } else {
      container.insertBefore(this._dragItem, item)
    }
  }

  rankDrop(e) {
    e.preventDefault()
    this._saveRankOrder(e.currentTarget.closest("[data-ranking-field]"))
  }

  rankDragEnd(e) {
    e.currentTarget.classList.remove("opacity-50", "scale-95")
    this._saveRankOrder(e.currentTarget.closest("[data-ranking-field]"))
    this._dragItem = null
  }

  // Touch drag for mobile
  rankTouchStart(e) {
    const item = e.currentTarget
    this._touchItem = item
    this._touchStartY = e.touches[0].clientY
    this._touchClone = item.cloneNode(true)
    this._touchClone.style.cssText = `position:fixed;z-index:9999;pointer-events:none;width:${item.offsetWidth}px;opacity:0.85;transform:scale(1.02);`
    this._touchClone.classList.add("shadow-lg")
    document.body.appendChild(this._touchClone)
    this._updateTouchClonePos(e.touches[0])
    item.classList.add("opacity-30")
  }

  rankTouchMove(e) {
    if (!this._touchItem) return
    e.preventDefault()
    const touch = e.touches[0]
    this._updateTouchClonePos(touch)

    const container = this._touchItem.closest("[data-ranking-field]")
    const items = [...container.children].filter(el => el !== this._touchItem)
    for (const sibling of items) {
      const rect = sibling.getBoundingClientRect()
      const midY = rect.top + rect.height / 2
      if (touch.clientY < midY) {
        container.insertBefore(this._touchItem, sibling)
        return
      }
    }
    container.appendChild(this._touchItem)
  }

  rankTouchEnd(e) {
    if (!this._touchItem) return
    this._touchItem.classList.remove("opacity-30")
    if (this._touchClone) {
      this._touchClone.remove()
      this._touchClone = null
    }
    this._saveRankOrder(this._touchItem.closest("[data-ranking-field]"))
    this._touchItem = null
  }

  _updateTouchClonePos(touch) {
    if (!this._touchClone) return
    this._touchClone.style.left = `${touch.clientX - this._touchClone.offsetWidth / 2}px`
    this._touchClone.style.top = `${touch.clientY - 24}px`
  }

  _saveRankOrder(container) {
    if (!container) return
    const fieldId = container.dataset.fieldId
    const newOrder = [...container.querySelectorAll("[data-rank-value]")].map(el => el.dataset.rankValue)
    this.answers[fieldId] = newOrder
    // Update rank number badges
    container.querySelectorAll("[data-rank-value]").forEach((el, i) => {
      const badge = el.querySelector("span")
      if (badge) badge.textContent = i + 1
    })
  }

  selectPollOption(e) {
    const fieldId = e.currentTarget.dataset.fieldId
    const value = e.currentTarget.dataset.optionValue
    const isMulti = e.currentTarget.dataset.multi === "true"

    if (isMulti) {
      const current = Array.isArray(this.answers[fieldId]) ? [...this.answers[fieldId]] : []
      const idx = current.indexOf(value)
      if (idx > -1) current.splice(idx, 1)
      else current.push(value)
      this.answers[fieldId] = current
    } else {
      this.answers[fieldId] = value
    }

    this.render()
  }

  // ==================
  // Navigation (multi-step)
  // ==================

  nextStep() {
    if (!this.validateCurrentStep()) return

    const stepIdx = this.steps.findIndex(s => s.id === this.currentStep)
    if (stepIdx < this.steps.length - 1) {
      this.currentStep = this.steps[stepIdx + 1].id
      this.saveDraft()
      this.render()
    }
  }

  previousStep() {
    const stepIdx = this.steps.findIndex(s => s.id === this.currentStep)
    if (stepIdx > 0) {
      this.currentStep = this.steps[stepIdx - 1].id
      this.render()
    }
  }

  validateCurrentStep() {
    const stepFields = this.fields.filter(f => f.step === this.currentStep)
    for (const field of stepFields) {
      const answer = this.answers[field.id]
      const isEmpty = !answer || (Array.isArray(answer) && answer.length === 0) || answer === ""

      if (field.required && isEmpty) {
        this._showFieldError(field.id, "Please fill this in")
        return false
      }

      if (!isEmpty) {
        const formatError = this._validateFieldFormat(field, answer)
        if (formatError) {
          this._showFieldError(field.id, formatError)
          return false
        }
      }
    }
    return true
  }

  // ==================
  // Submit & Draft
  // ==================

  async submit() {
    if (this.submitting) return

    // Validate all required fields
    const fieldsToValidate = this.formTypeValue === "multi_step"
      ? this.fields.filter(f => f.step === this.currentStep)
      : this.getVisibleFields()

    for (const field of fieldsToValidate) {
      const answer = this.answers[field.id]
      const isEmpty = !answer || (Array.isArray(answer) && answer.length === 0) || answer === ""

      if (field.required && isEmpty) {
        this._showFieldError(field.id, "Please fill this in")
        return
      }

      if (!isEmpty) {
        const formatError = this._validateFieldFormat(field, answer)
        if (formatError) {
          this._showFieldError(field.id, formatError)
          return
        }
      }
    }

    this.submitting = true
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = true
      this.submitButtonTarget.innerHTML = `<svg class="animate-spin w-4 h-4 inline-block mr-2" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>Submitting...`
    }

    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.content
      const response = await fetch(this.submitUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": token,
          "Accept": "application/json"
        },
        body: JSON.stringify({ answers: this.answers })
      })

      if (response.ok) {
        if (this.resultsUrlValue && this.formTypeValue === "polling") {
          window.location.href = this.resultsUrlValue
        } else {
          window.location.reload()
        }
      } else {
        const data = await response.json()
        alert(data.error || "Failed to submit. Please try again.")
        this.submitting = false
        if (this.hasSubmitButtonTarget) {
          this.submitButtonTarget.disabled = false
          this.submitButtonTarget.textContent = "Submit"
        }
      }
    } catch (err) {
      console.error("Submit error:", err)
      alert("An error occurred. Please try again.")
      this.submitting = false
      if (this.hasSubmitButtonTarget) {
        this.submitButtonTarget.disabled = false
        this.submitButtonTarget.textContent = "Submit"
      }
    }
  }

  async saveDraft() {
    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.content
      await fetch(this.saveDraftUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": token,
          "Accept": "application/json"
        },
        body: JSON.stringify({
          answers: this.answers,
          current_step: this.currentStep
        })
      })
    } catch (err) {
      console.error("Draft save error:", err)
    }
  }

  // ==================
  // Utilities
  // ==================

  _validateFieldFormat(field, answer) {
    const val = String(answer).trim()
    switch (field.type) {
      case "email":
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) return "Please enter a valid email address"
        break
      case "phone":
        if (!/^[+]?[\d\s\-().]{7,20}$/.test(val)) return "Please enter a valid phone number"
        break
      case "url":
        if (!/^https?:\/\/.+\..+/.test(val)) return "Please enter a valid URL (e.g. https://example.com)"
        break
    }
    return null
  }

  _showFieldError(fieldId, message) {
    const el = this.fieldsContainerTarget.querySelector(`[data-renderer-field-id="${fieldId}"]`)
    if (!el) return
    el.classList.add("animate-shake")
    if (!el.querySelector(".field-error-msg")) {
      const msg = document.createElement("p")
      msg.className = "field-error-msg text-sm text-error-500 mt-2"
      msg.textContent = message
      el.appendChild(msg)
    }
    setTimeout(() => {
      el.classList.remove("animate-shake")
      el.querySelector(".field-error-msg")?.remove()
    }, 3000)
  }

  esc(str) {
    if (!str) return ""
    const div = document.createElement("div")
    div.textContent = String(str)
    return div.innerHTML
  }
}
